'use client';

import type { Item } from '@swishapp/api-client';
import { useMemo } from 'react';
import { useWishlistItems } from '../../../../wishlist/client';
import type { SharedWishlistCardData } from '../../../wishlist-card';
import { useWishlistProducts } from './use-wishlist-products';
import type { WishlistItemWithProduct } from './wishlist-types';

export function useWishlistItemsWithProducts() {
  const {
    data: allItems,
    isLoading: isItemsLoading,
    error: itemsError,
  } = useWishlistItems();
  const {
    data: products,
    isLoading: isProductsLoading,
    error: productsError,
  } = useWishlistProducts();

  // Combine wishlist items with their corresponding product data
  const itemsWithProducts = useMemo(() => {
    if (!allItems || !products) return [];

    return allItems.map((item: Item): WishlistItemWithProduct => {
      // Find the corresponding product by matching GIDs
      const productGid = `gid://shopify/Product/${item.productId}`;
      const variantGid = `gid://shopify/ProductVariant/${item.variantId}`;

      const product = products.find((p) => {
        // First check if product matches
        if (p.productGid !== productGid) return false;

        // Then validate that the specific variant exists in this product
        // If variantGid is null, the product doesn't have variant-specific data
        // In that case, we'll allow it for backward compatibility
        if (p.variantGid && p.variantGid !== variantGid) return false;

        return true;
      });

      // Transform product data to match SharedWishlistCardData interface
      const transformedProduct: SharedWishlistCardData | undefined = product
        ? ({
            _id: product._id,
            title: product.title,
            slug: product.slug,
            sku: product.sku,
            size: product.size,
            squareMetersPerBox: product.squareMetersPerBox,
            price: product.price,
            compareAtPrice: product.compareAtPrice,
            brand: product.brand,
            productGid: product.productGid,
            variantGid: product.variantGid,
            mainImage: product.mainImage,
          } as SharedWishlistCardData)
        : undefined;

      return {
        id: item.id,
        productId: item.productId,
        variantId: typeof item.variantId === 'number' ? item.variantId : 0,
        quantity: item.quantity,
        product: transformedProduct,
      };
    });
  }, [allItems, products]);

  // Filter out items that don't have product data (in case of data inconsistencies)
  const validItemsWithProducts = useMemo(() => {
    return itemsWithProducts.filter((item) => item.product !== undefined);
  }, [itemsWithProducts]);

  // Separate items by issue type for better error handling
  const itemsWithMissingProducts = useMemo(() => {
    return itemsWithProducts.filter((item) => {
      if (!item.product) {
        const productGid = `gid://shopify/Product/${item.productId}`;
        const hasMatchingProduct = products?.some(
          (p) => p.productGid === productGid
        );

        // Mark as missing product data if no product found at all
        return !hasMatchingProduct;
      }
      return false;
    });
  }, [itemsWithProducts, products]);

  const itemsWithVariantIssues = useMemo(() => {
    return itemsWithProducts.filter((item) => {
      if (!item.product) {
        const productGid = `gid://shopify/Product/${item.productId}`;
        const variantGid = `gid://shopify/ProductVariant/${item.variantId}`;
        const matchingProduct = products?.find(
          (p) => p.productGid === productGid
        );

        // Mark as variant issue if product exists but variant doesn't match
        return (
          matchingProduct?.variantGid &&
          matchingProduct.variantGid !== variantGid
        );
      }
      return false;
    });
  }, [itemsWithProducts, products]);

  const error = itemsError || productsError;

  return {
    items: validItemsWithProducts,
    isLoading: isItemsLoading || isProductsLoading,
    error,
    totalItems: allItems?.length || 0,
    itemsWithMissingProducts,
    itemsWithVariantIssues,
  };
}
